'use client';
import React from 'react';

import { useAnalytics } from '@/integrations';
import { MakeModel } from '@/types/filter.types';
import {
    priceDefaultMax,
    priceDefaultMin,
    TFilterTerm,
} from '@/utils/filterUtil';
import { CarListingData } from '@/types';
import { AvailableCheckClient } from '@/utils/clientUtil';

const useFilter = (
    comboList: MakeModel,
    filters: TFilterTerm<CarListingData>[],
    setFilters: React.Dispatch<
        React.SetStateAction<TFilterTerm<CarListingData>[]>
    >
) => {
    const { trackEvent } = useAnalytics();

    let makeFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let modelFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let bodyStyleFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let transmissionFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let kilometersFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let yearFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let priceFilter: TFilterTerm<CarListingData> | undefined = undefined;
    let statusFilter: TFilterTerm<CarListingData> | undefined = undefined;

    for (const f of filters) {
        if (!f.keyValue || f.keyValue === '') {
            continue;
        }
        switch (f.keyName) {
            case 'make':
                makeFilter = f;
                break;
            case 'model':
                modelFilter = f;
                break;
            case 'bodyStyle':
                bodyStyleFilter = f;
                break;
            case 'transmission':
                transmissionFilter = f;
                break;
            case 'kilometers':
                kilometersFilter = f;
                break;
            case 'year':
                yearFilter = f;
                break;
            case 'sylndrSellingPrice':
                priceFilter = f;
                break;
            case 'id':
                statusFilter = f;
                break;
        }
    }

    const makes: string[] = makeFilter ? (makeFilter.keyValue as string[]) : [];
    const models: string[] = modelFilter
        ? (modelFilter.keyValue as string[])
        : [];
    const bodyStyle: string[] = bodyStyleFilter
        ? (bodyStyleFilter.keyValue as string[])
        : [];
    const year: number[] = yearFilter
        ? Array.isArray(yearFilter.keyValue)
            ? (yearFilter.keyValue as number[])
            : [yearFilter.keyValue as number]
        : [];
    const price: number[] = priceFilter
        ? (priceFilter.keyValue as number[])
        : [];
    const transmission: string[] = transmissionFilter
        ? (transmissionFilter.keyValue as string[])
        : [];
    const kilometers = kilometersFilter
        ? (kilometersFilter.keyValue as number)
        : 0;
    const status = statusFilter ? statusFilter.keyValue : '';

    const priceCon =
        priceFilter &&
        typeof priceFilter.keyValue === 'object' &&
        (priceFilter.keyValue[0] !== priceDefaultMin ||
            priceFilter.keyValue[1] !== priceDefaultMax);

    const filterObjectNotEmpty =
        !!makeFilter ||
        !!modelFilter ||
        !!bodyStyleFilter ||
        !!transmissionFilter ||
        !!kilometersFilter ||
        !!yearFilter ||
        !!priceCon ||
        !!statusFilter;

    //populate models for makeOnly Queries
    if (makeFilter && Array.isArray(makeFilter.keyValue) && !modelFilter) {
        makeFilter.keyValue = makeFilter.keyValue as string[];
        const modelsToAdd = makeFilter.keyValue
            .map(make => comboList[make]?.models)
            .flat();
        //
        filters.push({
            isEnabled: true,
            keyName: 'model',
            keyValue: [...modelsToAdd],
            compareType: 'in',
        });

        setFilters([...filters]);
    }

    const makeModelValueChanged = (vehicle: {
        make: string;
        model: string;
    }) => {
        let { make, model } = vehicle;
        const makeOnly = model === '';
        make = make.toLowerCase();
        model = model.toLowerCase();

        if (!makeFilter && makeOnly) {
            filters.push({
                isEnabled: true,
                keyName: 'make',
                keyValue: [make],
                compareType: 'in',
            });
        }

        //case 1.5: no make filter and model is clicked
        else if (!makeFilter && !makeOnly) {
            filters.push({
                isEnabled: true,
                keyName: 'make',
                keyValue: [make],
                compareType: 'in',
            });

            filters.push({
                isEnabled: true,
                keyName: 'model',
                keyValue: [model],
                compareType: 'in',
            });
        }

        //makeFilter exists cases
        else if (makeFilter && Array.isArray(makeFilter.keyValue)) {
            makeFilter.keyValue = makeFilter.keyValue as string[];

            //case 2: make clicked and it's already in makeFilter
            if (makeFilter.keyValue.includes(make) && makeOnly) {
                makeFilter.keyValue = makeFilter.keyValue.filter(
                    m => m !== make
                );

                //clear all associated models
                if (modelFilter && Array.isArray(modelFilter.keyValue)) {
                    modelFilter.keyValue = modelFilter.keyValue as string[];

                    modelFilter.keyValue = modelFilter.keyValue.filter(
                        model => !comboList[make]?.models.includes(model)
                    );
                }
            }

            //case 2.5: make clicked and it's not in filter
            else if (!makeFilter.keyValue.includes(make) && makeOnly) {
                makeFilter.keyValue.push(make);
                if (modelFilter && Array.isArray(modelFilter.keyValue)) {
                    modelFilter.keyValue = modelFilter.keyValue as string[];
                    modelFilter.keyValue.push(...comboList[make]?.models);
                }
            }

            if (modelFilter && Array.isArray(modelFilter.keyValue)) {
                modelFilter.keyValue = modelFilter.keyValue as string[];
                //case 3: model clicked and it's not in filters
                if (!modelFilter.keyValue.includes(model) && !makeOnly) {
                    modelFilter.keyValue.push(model);

                    if (!makeFilter.keyValue.includes(make)) {
                        makeFilter.keyValue.push(make);
                    }
                }
                //case 3.5: model clicked and its in filters
                else if (modelFilter.keyValue.includes(model) && !makeOnly) {
                    modelFilter.keyValue = modelFilter.keyValue.filter(
                        f => f !== model
                    );
                }
            }

            makeFilter.keyValue = makeFilter.keyValue.filter(make =>
                comboList[make]?.models.some(m => {
                    if (modelFilter) {
                        modelFilter.keyValue = modelFilter.keyValue as string[];
                        return modelFilter.keyValue.includes(m);
                    } else {
                        return false;
                    }
                })
            );

            cleanUpMakeModel();
        }

        setFilters([...filters]);
    };

    const cleanUpMakeModel = () => {
        if (makeFilter && Array.isArray(makeFilter.keyValue)) {
            makeFilter.keyValue = makeFilter.keyValue as string[];
            //cleanup: remove makes with no models
            makeFilter.keyValue = makeFilter.keyValue.filter(make =>
                comboList[make]?.models.some(m => {
                    if (modelFilter) {
                        modelFilter.keyValue = modelFilter.keyValue as string[];
                        return modelFilter.keyValue.includes(m);
                    } else {
                        return false;
                    }
                })
            );

            //cleanup: remove make and model filters if there is no models selected
            if (makeFilter.keyValue.length === 0) {
                filters = filters.filter(
                    f => f.keyName !== 'model' && f.keyName !== 'make'
                );
            }
        }
    };

    const kilometersValueChanged = (text: string) => {
        let updatedFilters = filters;
        if (kilometersFilter) {
            if (kilometersFilter.keyValue === Number(text)) {
                updatedFilters = updatedFilters.filter(
                    f => f.keyName !== 'kilometers'
                );
            } else {
                kilometersFilter.keyValue = Number(text);
            }
        } else {
            updatedFilters.push({
                isEnabled: true,
                keyName: 'kilometers',
                keyValue: Number(text),
                compareType: 'lte',
            });
        }
        setFilters([...updatedFilters]);
    };

    const availableValueChanged = (text: string) => {
        if (statusFilter) {
            if (statusFilter.keyValue === text) {
                const index = filters.indexOf(statusFilter, 0);
                if (index > -1) {
                    filters.splice(index, 1);
                }
                statusFilter = undefined;
            } else {
                statusFilter.keyValue = text.toLowerCase();
            }
        } else {
            filters.push({
                isEnabled: true,
                keyName: 'id',
                keyValue: text.toLowerCase(),
                fn: AvailableCheckClient,
                compareType: 'function',
            });
        }

        setFilters([...filters]);
    };
    const bodyTypeValueChanged = (text: string) => {
        if (bodyStyleFilter) {
            bodyStyleFilter.keyValue = bodyStyleFilter.keyValue as string[];
            bodyStyleFilter.compareType = 'in';

            if (bodyStyleFilter.keyValue.includes(text)) {
                bodyStyleFilter.keyValue = bodyStyleFilter.keyValue.filter(
                    (e: string) => e !== text
                );
            } else {
                bodyStyleFilter.keyValue.push(text);
            }
        } else {
            filters.push({
                isEnabled: true,
                keyName: 'bodyStyle',
                keyValue: [text],
                compareType: 'in',
            });
        }

        if (
            bodyStyleFilter &&
            Array.isArray(bodyStyleFilter.keyValue) &&
            bodyStyleFilter.keyValue.length === 0
        ) {
            setFilters([...filters.filter(f => f.keyName !== 'bodyStyle')]);
        } else {
            setFilters([...filters]);
        }
    };
    // changes transmission value based on the latest input
    const transmissionValueChanged = (text: string) => {
        if (transmissionFilter) {
            transmissionFilter.keyValue =
                transmissionFilter.keyValue as string[];
            transmissionFilter.compareType = 'in';

            if (transmissionFilter.keyValue.includes(text)) {
                transmissionFilter.keyValue =
                    transmissionFilter.keyValue.filter(
                        (e: string) => e !== text
                    );
            } else {
                transmissionFilter.keyValue.push(text);
            }
        } else {
            filters.push({
                isEnabled: true,
                keyName: 'transmission',
                keyValue: [text],
                compareType: 'in',
            });
        }

        if (
            transmissionFilter &&
            Array.isArray(transmissionFilter.keyValue) &&
            transmissionFilter.keyValue.length === 0
        ) {
            setFilters([...filters.filter(f => f.keyName !== 'transmission')]);
        } else {
            setFilters([...filters]);
        }
    };

    // changes price range based on the latest input
    const priceRangeChanged = (value: number[]) => {
        if (priceFilter) {
            priceFilter.keyValue = value;
        } else {
            filters.push({
                isEnabled: true,
                keyName: 'sylndrSellingPrice',
                keyValue: value,
                compareType: 'range',
            });
        }

        if (
            priceFilter &&
            Array.isArray(priceFilter.keyValue) &&
            priceFilter.keyValue[0] === priceDefaultMin &&
            priceFilter.keyValue[1] === priceDefaultMax
        ) {
            filters = filters.filter(f => f.keyName !== 'sylndrSellingPrice');
        }
        setFilters([...filters]);
    };

    // change year range based on the latest input
    const yearRangeChanged = (value: number[]) => {
        if (value.length === 1) {
            if (yearFilter) {
                yearFilter.keyValue = value[0];
                yearFilter.compareType = 'gte';
            } else {
                filters.push({
                    isEnabled: true,
                    keyName: 'year',
                    keyValue: value[0],
                    compareType: 'gte',
                });
            }
        }
        if (value.length === 2) {
            if (yearFilter) {
                yearFilter.keyValue = value;
                yearFilter.compareType = 'range';
            } else {
                filters.push({
                    isEnabled: true,
                    keyName: 'year',
                    keyValue: [value[0], value[1]],
                    compareType: 'range',
                });
            }
        }

        setFilters([...filters]);
    };

    const kilometersTagClicked = () => {
        setFilters([...filters.filter(f => f.keyName !== 'kilometers')]);
    };
    const availableTagClicked = () => {
        setFilters([...filters.filter(f => f.keyName !== 'id')]);
    };
    const transmissionTagClicked = (text: string | number) => {
        text = String(text);

        if (transmissionFilter && transmission.length > 0) {
            transmissionFilter.keyValue = transmission.filter(e => e !== text);
        }

        if (
            transmissionFilter &&
            Array.isArray(transmissionFilter.keyValue) &&
            transmissionFilter.keyValue.length === 0
        ) {
            setFilters([...filters.filter(f => f.keyName !== 'transmission')]);
        } else {
            setFilters([...filters]);
        }
    };
    const bodyStyleTagClicked = (text: string | number) => {
        text = String(text);

        if (bodyStyleFilter && bodyStyle.length > 0) {
            bodyStyleFilter.keyValue = bodyStyle.filter(e => e !== text);
        }

        if (
            bodyStyleFilter &&
            Array.isArray(bodyStyleFilter.keyValue) &&
            bodyStyleFilter.keyValue.length === 0
        ) {
            setFilters([...filters.filter(f => f.keyName !== 'bodyStyle')]);
        } else {
            setFilters([...filters]);
        }
    };
    const yearTagClicked = () => {
        filters = filters.filter(f => f.keyName !== 'year');
        setFilters([...filters]);
    };

    const priceTagClicked = () => {
        filters = filters.filter(f => f.keyName !== 'sylndrSellingPrice');

        setFilters([...filters]);
    };
    const makeTagClicked = (text: string | number) => {
        const make = String(text).toLowerCase();
        if (makeFilter && Array.isArray(makeFilter.keyValue)) {
            makeFilter.keyValue = makeFilter.keyValue as string[];
            makeFilter.keyValue = makeFilter.keyValue.filter(mk => mk !== make);
            if (modelFilter && Array.isArray(modelFilter.keyValue)) {
                modelFilter.keyValue = modelFilter.keyValue as string[];
                modelFilter.keyValue = modelFilter.keyValue.filter(
                    m => !comboList[make]?.models.includes(m)
                );
            }
        }
        cleanUpMakeModel();
        setFilters([...filters]);
    };
    const modelTagClicked = (vehicle: { make: string; model: string }) => {
        if (modelFilter && Array.isArray(modelFilter.keyValue)) {
            modelFilter.keyValue = modelFilter.keyValue as string[];
            modelFilter.keyValue = modelFilter.keyValue.filter(
                m => m !== vehicle.model
            );
        }

        cleanUpMakeModel();
        setFilters([...filters]);
    };

    const clearFilters = () => {
        trackEvent('Action: Clear Filter');

        if (setFilters) {
            setFilters([]);
        }
    };

    return {
        makeModelValueChanged,
        kilometersValueChanged,
        bodyTypeValueChanged,
        transmissionValueChanged,
        priceRangeChanged,
        yearRangeChanged,
        availableValueChanged,
        availableTagClicked,
        kilometersTagClicked,
        transmissionTagClicked,
        bodyTypeTagClicked: bodyStyleTagClicked,
        yearTagClicked,
        priceTagClicked,
        makeTagClicked,
        modelTagClicked,
        clearFilters,
        filterObjectNotEmpty,
        makes,
        models,
        year,
        price,
        transmission,
        bodyType: bodyStyle,
        kilometers,
        status,
    };
};

export default useFilter;
